/**
 * SigningEnvelopeDetailPage
 * Landlord detail view: header card, recipient list, action bar, audit trail
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MoreVertical, CircleCheck, X } from 'lucide-react';
import {
  getSigningEnvelope,
  sendEnvelope,
  cancelEnvelope,
  resendToRecipient,
  downloadEnvelopeDocument,
} from '../../lib/api';
import type { EnvelopeDetailResponse } from '@resida/api-types/lease-support';
import { PageHeader } from '../../components/layout/index';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { formatDateTime } from '../../utils/date';
import { useAuth } from '../../hooks/useAuth';
import { recipientStatusLabel, envelopeStatusLabel } from '../../lib/recipient-status';
import { readSigningResult, signingCeremonyPath, signingEnvelopePath, type SigningLaunchState } from '../../utils/signing-navigation';

// Derived from EnvelopeDetailResponseSchema (which nests RecipientResponseSchema) in
// @resida/api-types/lease-support, rather than hand-redeclared — see that module's header
// comment and packages/api-types/README.md.
type EnvelopeDetail = EnvelopeDetailResponse;

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'DRAFT': return 'bg-muted text-muted-foreground border-border';
    case 'PENDING': return 'bg-info/15 text-info border-info/25';
    case 'COMPLETED': return 'bg-success/15 text-success border-success/25';
    case 'CANCELLED': return 'bg-destructive/15 text-destructive border-destructive/25';
    case 'EXPIRED': return 'bg-warning/15 text-warning border-warning/25';
    case 'ERROR': return 'bg-destructive/15 text-destructive border-destructive/25';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}

function recipientStatusBadgeClass(status: string): string {
  switch (status) {
    case 'SIGNED': return 'bg-success/15 text-success border-success/25';
    case 'PENDING': return 'bg-warning/15 text-warning border-warning/25';
    case 'SENT': return 'bg-info/15 text-info border-info/25';
    case 'OPENED': return 'bg-info/15 text-info border-info/25';
    case 'DECLINED': return 'bg-destructive/15 text-destructive border-destructive/25';
    case 'ERROR': return 'bg-destructive/15 text-destructive border-destructive/25';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}

interface HeaderAction {
  key: string;
  label: string;
  testId: string;
  onClick: () => void;
  disabled: boolean;
  ariaLabel?: string;
  destructive?: boolean;
  variant?: 'default' | 'outline';
}

// Matches Tailwind's `sm:` breakpoint. Below it, the header collapses to the primary action
// plus an overflow menu (FM3) instead of duplicating every action into both a CSS-hidden
// desktop row and a CSS-hidden mobile row — duplicating them would leave two DOM nodes per
// action sharing the same data-testid, which breaks single-match testid queries.
const HEADER_ACTIONS_BREAKPOINT_QUERY = '(min-width: 640px)';

function HeaderActionButton({ action }: { action: HeaderAction }) {
  return (
    <Button
      data-testid={action.testId}
      aria-label={action.ariaLabel}
      onClick={action.onClick}
      disabled={action.disabled}
      variant={action.variant ?? 'default'}
      className={action.destructive ? 'text-destructive border-destructive/50 hover:bg-destructive/10' : undefined}
    >
      {action.label}
    </Button>
  );
}

export function SigningEnvelopeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { manager } = useAuth();

  const [envelope, setEnvelope] = useState<EnvelopeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [notFound, setNotFound] = useState(false);

  // Landlord in-app signing (BR-DS-02 / BR-DS-13) happens on the full-screen route
  // `/signing/:id/sign` (SigningCeremonyPage); Sign navigates there. When it hands control back it
  // passes `{ signingResult }` in router state, which drives the success confirmation below
  // (GitHub issue #1063): shown once the envelope has been re-fetched, so the message reflects the
  // CURRENT envelope status (COMPLETED vs. other recipients still pending).
  const location = useLocation();
  const [showSignSuccess, setShowSignSuccess] = useState(
    () => readSigningResult(location.state) === 'completed'
  );
  useEffect(() => {
    // Consume the one-shot router state so a reload/back does not replay the banner.
    if (readSigningResult(location.state)) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drives which header-action layout renders (FM3): below the `sm:` breakpoint only the
  // primary action plus an overflow menu render; at/above it every action renders inline.
  // JS-gated (rather than a CSS-only hidden/flex pair) so exactly one DOM node exists per
  // action at a time.
  const [isNarrowHeaderViewport, setIsNarrowHeaderViewport] = useState(
    () => !window.matchMedia(HEADER_ACTIONS_BREAKPOINT_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(HEADER_ACTIONS_BREAKPOINT_QUERY);
    const handleChange = () => setIsNarrowHeaderViewport(!mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Announced via an aria-live region (same content as the visual success banner).
  const signModeAnnouncement = showSignSuccess
    ? (envelope?.status === 'COMPLETED'
        ? 'Signature recorded. This document is now fully signed.'
        : 'Signature recorded. Other recipients still need to sign.')
    : '';

  const fetchEnvelope = useCallback(async (): Promise<EnvelopeDetail | null> => {
    if (!id) return null;
    try {
      setIsLoading(true);
      setError(null);
      const res = await getSigningEnvelope(id) as EnvelopeDetail;
      setEnvelope(res);
      return res;
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 404) {
        setNotFound(true);
      } else {
        setError(apiErr?.message || 'Failed to load envelope');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEnvelope();
  }, [fetchEnvelope]);

  async function handleSend() {
    if (!id) return;
    try {
      setIsSending(true);
      setActionError(null);
      await sendEnvelope(id);
      await fetchEnvelope();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to send envelope');
    } finally {
      setIsSending(false);
    }
  }

  async function handleCancel() {
    if (!id || !envelope) return;
    if (!window.confirm(`Cancel signing envelope "${envelope.title}"? This cannot be undone.`)) return;
    try {
      setIsCancelling(true);
      setActionError(null);
      setShowSignSuccess(false);
      await cancelEnvelope(id);
      await fetchEnvelope();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel envelope');
    } finally {
      setIsCancelling(false);
    }
  }

  async function handleResend(recipientId: string) {
    if (!id) return;
    try {
      setResendingId(recipientId);
      setActionError(null);
      await resendToRecipient(id, recipientId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  }

  async function handleDownload() {
    if (!id) return;
    try {
      setIsDownloading(true);
      setActionError(null);
      const response = await downloadEnvelopeDocument(id);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-document-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setIsDownloading(false);
    }
  }

  function handleStartSign() {
    if (!id) return;
    setActionError(null);
    setShowSignSuccess(false);
    const state: SigningLaunchState = { returnTo: signingEnvelopePath(id) };
    navigate(signingCeremonyPath(id), { state });
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="bg-card rounded-lg border border-border p-8 space-y-3">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-px w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="bg-card rounded-lg border border-border p-16 text-center">
          <h2 className="text-xl font-semibold text-foreground mb-3">Envelope Not Found</h2>
          <p className="text-sm text-muted-foreground mb-6">
            The signing envelope you are looking for does not exist or you do not have access to it.
          </p>
          <Button
            onClick={() => navigate('/signing')}
            variant="outline"
          >
            Back to Signing Envelopes
          </Button>
        </div>
      </div>
    );
  }

  if (error && !envelope) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <p className="text-destructive text-sm mb-4">{error}</p>
          <Button onClick={fetchEnvelope}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!envelope) return null;

  const canSend = envelope.status === 'DRAFT';
  const canCancel = envelope.status === 'DRAFT' || envelope.status === 'PENDING';
  const canDownload = envelope.status === 'COMPLETED' && !!envelope.documentId;

  const myRecipient = envelope.recipients.find(
    (r) => r.recipientType === 'manager' && r.recipientId === manager?.id
  );
  const canSign =
    envelope.status === 'PENDING' &&
    !!myRecipient &&
    (myRecipient.status === 'SENT' || myRecipient.status === 'OPENED');

  const headerActions: HeaderAction[] = [];
  if (canSign) {
    headerActions.push({
      key: 'sign',
      label: 'Sign',
      testId: 'signing-detail-sign-btn',
      ariaLabel: 'Sign document',
      onClick: handleStartSign,
      disabled: false,
    });
  }
  if (canSend) {
    headerActions.push({
      key: 'send',
      label: isSending ? 'Sending...' : 'Send for Signing',
      testId: 'signing-detail-send-btn',
      onClick: handleSend,
      disabled: isSending,
    });
  }
  if (canDownload) {
    headerActions.push({
      key: 'download',
      label: isDownloading ? 'Downloading...' : 'Download Signed Document',
      testId: 'signing-detail-download-btn',
      onClick: handleDownload,
      disabled: isDownloading,
      variant: 'outline',
    });
  }
  if (canCancel) {
    headerActions.push({
      key: 'cancel',
      label: isCancelling ? 'Cancelling...' : 'Cancel',
      testId: 'signing-detail-cancel-btn',
      onClick: handleCancel,
      disabled: isCancelling,
      variant: 'outline',
      destructive: true,
    });
  }
  // FM3 (blocking, mobile): on narrow viewports only the primary action renders inline;
  // everything else moves into an overflow menu so the envelope title keeps enough width to
  // stay legible instead of truncating to ~8 characters.
  const [primaryHeaderAction, ...secondaryHeaderActions] = headerActions;

  return (
    <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]" data-testid="signing-envelope-detail-page">
      <PageHeader
        title={envelope.title}
        subtitle={`${envelope.sourceType}: ${envelope.sourceId}`}
        backLink={{ label: 'Signing Envelopes', to: '/signing' }}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className={`font-semibold ${statusBadgeClass(envelope.status)}`}
            >
              {envelopeStatusLabel(envelope.status)}
            </Badge>
            {isNarrowHeaderViewport ? (
              // Mobile: only the primary action renders inline; the rest collapse into an
              // overflow menu so the title has room to stay legible (FM3).
              <>
                {primaryHeaderAction && <HeaderActionButton action={primaryHeaderAction} />}
                {secondaryHeaderActions.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-11 w-11"
                        aria-label="More actions"
                        data-testid="signing-detail-more-actions-btn"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {secondaryHeaderActions.map((action) => (
                        <DropdownMenuItem
                          key={action.key}
                          data-testid={action.testId}
                          disabled={action.disabled}
                          onSelect={action.onClick}
                          className={`min-h-[44px] ${action.destructive ? 'text-destructive focus:text-destructive' : ''}`}
                        >
                          {action.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            ) : (
              // Tablet/desktop: every available action shown inline (unchanged from before).
              headerActions.map((action) => (
                <HeaderActionButton key={action.key} action={action} />
              ))
            )}
          </div>
        }
      />

      {/* Announces the post-signing confirmation to assistive tech. */}
      <div aria-live="polite" className="sr-only">
        {signModeAnnouncement}
      </div>

      {actionError && (
        <div className="p-3 bg-destructive/10 border border-destructive/25 rounded-md text-sm text-destructive mb-5">
          {actionError}
        </div>
      )}

      {/* Header card */}
      <div className="bg-card rounded-lg border border-border p-6 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Created</p>
            <p className="text-sm font-medium text-foreground m-0">{formatDateTime(envelope.createdAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Sent</p>
            <p className="text-sm font-medium text-foreground m-0">{formatDateTime(envelope.sentAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Completed</p>
            <p className="text-sm font-medium text-foreground m-0">{formatDateTime(envelope.completedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Expires</p>
            <p className="text-sm font-medium text-foreground m-0">{formatDateTime(envelope.expiresAt)}</p>
          </div>
        </div>
      </div>

      <>
          {/* Post-signing success confirmation (GitHub issue #1063). Distinguishes the two
              possible outcomes off the just-refreshed envelope: fully COMPLETED (signed
              document downloadable) vs. this signature recorded with other recipients still
              pending. Announced via the SAME aria-live region as the consent/ceremony
              transitions above (signModeAnnouncement) rather than a second live region.
              Does not auto-dismiss (so it can't be missed) and never blocks the page —
              dismissed only by an explicit user action. */}
          {showSignSuccess && (
            <div
              data-testid="signing-detail-success"
              className="flex items-start gap-3 bg-success/10 border border-success/25 rounded-lg p-4 mb-5"
            >
              <CircleCheck className="h-5 w-5 text-success flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground m-0">
                  {envelope.status === 'COMPLETED'
                    ? 'Signature recorded — this document is now fully signed.'
                    : 'Your signature was recorded.'}
                </p>
                <p className="text-sm text-muted-foreground m-0 mt-0.5">
                  {envelope.status === 'COMPLETED'
                    ? 'The signed document is available to download using the button above.'
                    : 'Other recipients still need to sign before this document is complete.'}
                </p>
              </div>
              <Button
                data-testid="signing-detail-success-dismiss-btn"
                aria-label="Dismiss"
                onClick={() => setShowSignSuccess(false)}
                variant="ghost"
                size="icon"
                className="h-11 w-11 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Recipients */}
          <div className="bg-card rounded-lg border border-border p-6 mb-5">
            <h2 className="text-lg font-semibold text-foreground m-0 mb-4">Recipients</h2>
            {envelope.recipients.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recipients.</p>
            ) : (
              <div className="flex flex-col divide-y divide-border">
                {envelope.recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    data-testid={`signing-recipient-row-${recipient.id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-foreground m-0">{recipient.name}</p>
                        <Badge variant="outline" className="text-xs text-muted-foreground bg-muted border-border">
                          {recipient.role}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`text-xs font-semibold ${recipientStatusBadgeClass(recipient.status)}`}
                        >
                          {recipientStatusLabel(recipient.status)}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground m-0 mt-0.5">{recipient.email}</p>
                      {recipient.signedAt && (
                        <p className="text-xs text-success m-0 mt-0.5">
                          Signed {formatDateTime(recipient.signedAt)}
                        </p>
                      )}
                    </div>
                    {recipient.status === 'PENDING' || recipient.status === 'SENT' || recipient.status === 'OPENED' ? (
                      <Button
                        data-testid={`signing-recipient-resend-${recipient.id}`}
                        onClick={() => handleResend(recipient.id)}
                        disabled={resendingId === recipient.id}
                        variant="outline"
                        className="flex-shrink-0 px-3"
                      >
                        {resendingId === recipient.id ? 'Resending...' : 'Resend'}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Audit trail */}
          <div
            className="bg-card rounded-lg border border-border p-6"
            data-testid="signing-audit-trail"
          >
            <h2 className="text-lg font-semibold text-foreground m-0 mb-4">Audit Trail</h2>
            {envelope.auditEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No audit events.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {envelope.auditEvents.map((event) => (
                  <div key={event.id} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">{event.eventType}</span>
                      <span className="text-muted-foreground ml-2">{formatDateTime(event.occurredAt)}</span>
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5 m-0">
                          {Object.entries(event.metadata)
                            .map(([k, v]) => `${k}: ${String(v)}`)
                            .join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
      </>
    </div>
  );
}

export default SigningEnvelopeDetailPage;
